import type { ValidationError } from '@capture/shared';
import { ValidationFinding } from '@/features/admin/components/ValidationFinding';
import styles from './export-composer.module.css';

interface DestinationWarningsProps {
  mappings: Array<{ targetColumn: string; sourceField: string }>;
  fieldNames: string[];
  /** Pre-filtered to this destination's routingCondition path. */
  validationErrors?: ValidationError[];
}

export function DestinationWarnings({ mappings, fieldNames, validationErrors }: DestinationWarningsProps) {
  // Validator findings (new)
  const findings = validationErrors ?? [];

  // Existing ad-hoc warnings: duplicate sources + unmapped fields.
  const sourceUsage = new Map<string, string[]>();
  for (const m of mappings) {
    if (!m.sourceField) continue;
    const cols = sourceUsage.get(m.sourceField) ?? [];
    cols.push(m.targetColumn);
    sourceUsage.set(m.sourceField, cols);
  }
  const duplicates = Array.from(sourceUsage.entries()).filter(([, cols]) => cols.length > 1);
  const mappedSources = new Set(mappings.map((m) => m.sourceField).filter(Boolean));
  const unmapped = fieldNames.filter((f) => !mappedSources.has(f));

  const hasAdhoc = duplicates.length > 0 || unmapped.length > 0;
  const hasFindings = findings.length > 0;
  if (!hasAdhoc && !hasFindings) return null;

  const showHeaders = hasFindings && hasAdhoc;

  return (
    <div className={styles.mappingWarnings}>
      {hasFindings && (
        <>
          {showHeaders && <div className={styles.mappingWarningSectionHeader}>Routing condition issues</div>}
          {findings.map((err, i) => (
            <ValidationFinding key={`${err.path.join('.')}-${i}`} error={err} variant="inline" />
          ))}
        </>
      )}

      {hasAdhoc && (
        <>
          {showHeaders && <div className={styles.mappingWarningSectionHeader}>Mapping warnings</div>}
          {duplicates.map(([source, cols]) => (
            <div key={source} className={`${styles.mappingWarningItem} ${styles.mappingWarningDuplicate}`} role="alert">
              <span className={styles.mappingWarningIcon}>⚠</span>
              {source} is mapped to both {cols.join(' and ')}
            </div>
          ))}
          {unmapped.length > 0 && (
            <div className={`${styles.mappingWarningItem} ${styles.mappingWarningInfo}`} role="status">
              <span className={styles.mappingWarningIcon}>ℹ</span>
              {unmapped.length} source field{unmapped.length !== 1 ? 's' : ''} not included in export:{' '}
              {unmapped.join(', ')}
            </div>
          )}
        </>
      )}
    </div>
  );
}
